import { makeStyles } from "tss-react/mui";
import { IFieldSetStackedField } from "@miplatform/types";
import { getValueFormat } from "@miplatform/utils";
import { Box } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiStackedTypography } from "../MiStackedTypography/MiStackedTypography";

export function MiFieldSetStacked<T>({
  fieldset,
  row,
  rowStyle,
  fieldsetStyle,
  containerStyle,
  showNulls,
  doesCardExist,
  doesActionsExist,
  testId = "mi-fieldset-stacked",
}: MiFieldSetStackedProps<T>) {
  const { classes } = useStyles({
    rowStyle,
    fieldsetStyle,
  });

  const checkRenderFieldValue = (item: IFieldSetStackedField<T>) => {
    const { renderValue, ...rest } = item;
    const formattedValue =
      row && item.field
        ? getValueFormat(row[item.field], item.type)
        : item.value;

    return (
      <Box className={classes.container}>
        {item.renderIcon && item.renderIcon(row)}
        {item.icon && (
          <Box style={{ marginRight: 3 }}>
            <MiIcon
              path={item.icon}
              size={item.iconSize || 0.5}
              color={item.color || "grey"}
            />
          </Box>
        )}
        <MiStackedTypography
          title={item.label || ""}
          descriptionProps={{
            minHeight: 14,
            color: item.color,
            textAlign: item.textAlign,
            children: renderValue ? renderValue(row) : formattedValue ?? "",
            ...rest,
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            overflow: "hidden",
            ...(!renderValue && { width: "100%" }),
            ...(item.column && { display: "flex", flexDirection: "column" }),
            ...item.style,
          }}
        />
      </Box>
    );
  };

  const renderField = (
    item: IFieldSetStackedField<T>,
    index: number,
    testId: string
  ) => {
    if (item?.renderCell) {
      return (
        <Box
          key={`key_${index}`}
          data-testid={`${testId}-${index}`}
          sx={{ flex: item.flexSize || 1 }}
          minWidth={item.minWidth}
        >
          {item.renderCell(row)}
        </Box>
      );
    }

    if (
      showNulls ||
      (row && item.field && !!row[item.field]) ||
      !!item?.value
    ) {
      return (
        <Box
          key={`key_${index}`}
          data-testid={`${testId}-${index}`}
          minWidth={item.minWidth || 120}
          width={item.width}
          maxWidth={item.maxWidth}
          style={{
            flex: item.width ? "0 auto" : item.flexSize || 1,
            ...item.style,
          }}
        >
          {checkRenderFieldValue(item) ?? ""}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      className={classes.fieldsetContainer}
      style={containerStyle}
      data-testid={testId}
    >
      {doesCardExist && <div />}
      {fieldset?.map((item: IFieldSetStackedField<T>, index: number) =>
        renderField(item, index, testId)
      )}
      {doesActionsExist && <div />}
    </Box>
  );
}

const useStyles = makeStyles<{
  rowStyle?: React.CSSProperties;
  fieldsetStyle?: React.CSSProperties;
}>()((theme, { rowStyle, fieldsetStyle }) => ({
  container: {
    display: "flex",
    alignItems: "center",
    ...rowStyle,
  },
  fieldsetContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
    ...fieldsetStyle,
  },
}));

export interface MiFieldSetStackedProps<T> {
  fieldset: Array<IFieldSetStackedField<T>>;
  row: T;
  rowStyle?: React.CSSProperties;
  fieldsetStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  showNulls?: boolean;
  doesCardExist?: boolean;
  doesActionsExist?: boolean;
  testId?: string;
}
